import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type {
  BodyArea,
  Device,
  Treatment,
  TreatmentCategory,
} from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { Button } from '@/components/ui/button';
import { DurationInput } from '@/components/ui/durationInput';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { amountToSeconds, secondsToAmount, type DurationUnit } from '@/utils/durationFormatting';
import {
  useRemoveTreatmentImageMutation,
  useSaveTreatmentMutation,
  useUploadTreatmentImageMutation,
} from '@/hooks/useTreatmentLibraryMutations';
import { formatBilingualName, resolveLocalizedName } from './localizedName';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { EntityImageUpload } from './EntityImageUpload';

const DEFAULT_DURATION_UNIT: DurationUnit = 'min';

function isDurationUnit(value: string): value is DurationUnit {
  return value === 's' || value === 'min' || value === 'h';
}

function initialDurationAmount(treatment: Treatment | null): number {
  if (treatment === null || treatment.defaultDurationSeconds === null) {
    return 0;
  }
  const unit = isDurationUnit(treatment.defaultDurationUnit ?? '')
    ? (treatment.defaultDurationUnit as DurationUnit)
    : DEFAULT_DURATION_UNIT;
  return secondsToAmount(treatment.defaultDurationSeconds, unit);
}

function initialDurationUnit(treatment: Treatment | null): DurationUnit {
  if (treatment === null || treatment.defaultDurationUnit === null) {
    return DEFAULT_DURATION_UNIT;
  }
  return isDurationUnit(treatment.defaultDurationUnit)
    ? treatment.defaultDurationUnit
    : DEFAULT_DURATION_UNIT;
}

interface TreatmentFormModalProps {
  treatment: Treatment | null;
  categories: TreatmentCategory[];
  bodyAreas: BodyArea[];
  devices: Device[];
  onClose: () => void;
  onManageBodyAreas: () => void;
  onManageDevices: () => void;
}

function initialCategoryId(treatment: Treatment | null, categories: TreatmentCategory[]): string {
  if (treatment !== null && treatment.category !== null) {
    return treatment.category.id;
  }
  const firstCategory = categories[0];
  if (firstCategory !== undefined) {
    return firstCategory.id;
  }
  return '';
}

export function TreatmentFormModal({
  treatment,
  categories,
  bodyAreas,
  devices,
  onClose,
  onManageBodyAreas,
  onManageDevices,
}: TreatmentFormModalProps) {
  const { t } = useTranslation('treatmentLibrary');
  const isEditing = treatment !== null;
  const mutation = useSaveTreatmentMutation();

  const [nameEn, setNameEn] = useState(treatment === null ? '' : treatment.nameEn);
  const [namePl, setNamePl] = useState(treatment === null ? '' : treatment.namePl);
  const [categoryId, setCategoryId] = useState(initialCategoryId(treatment, categories));
  const [deviceId, setDeviceId] = useState(
    treatment === null || treatment.device === null ? '' : treatment.device.id
  );
  const [durationAmount, setDurationAmount] = useState(initialDurationAmount(treatment));
  const [durationUnit, setDurationUnit] = useState<DurationUnit>(initialDurationUnit(treatment));
  const [contraindication, setContraindication] = useState(
    treatment === null || treatment.contraindication === null ? '' : treatment.contraindication
  );
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>(
    treatment === null
      ? []
      : treatment.bodyAreas.map(function toId(area) {
          return area.id;
        })
  );
  const [isActive, setIsActive] = useState(treatment === null ? true : treatment.isActive);
  const [imageUrl, setImageUrl] = useState<string | null>(
    treatment === null ? null : treatment.imageUrl
  );
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImageRemoved, setPendingImageRemoved] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const uploadImage = useUploadTreatmentImageMutation();
  const removeImage = useRemoveTreatmentImageMutation();
  const isSaving = mutation.isPending || uploadImage.isPending || removeImage.isPending;

  function handleSelectImage(file: File) {
    setPendingImageFile(file);
    setPendingImageRemoved(false);
    setImageUrl(URL.createObjectURL(file));
  }

  function handleRemoveImage() {
    setPendingImageFile(null);
    setPendingImageRemoved(true);
    setImageUrl(null);
  }

  const nameEnMissing = nameEn.trim().length === 0;
  const namePlMissing = namePl.trim().length === 0;
  const selectedDevice = devices.find(function match(device) {
    return device.id === deviceId;
  });

  function toggleArea(areaId: string) {
    setSelectedAreaIds(function apply(previous) {
      return previous.includes(areaId)
        ? previous.filter(function keep(id) {
            return id !== areaId;
          })
        : [...previous, areaId];
    });
  }

  async function save() {
    if (nameEnMissing || namePlMissing) {
      setShowErrors(true);
      return;
    }
    try {
      const saved = await mutation.mutateAsync({
        treatmentId: treatment === null ? null : treatment.id,
        payload: {
          nameEn: nameEn.trim(),
          namePl: namePl.trim(),
          categoryId: categoryId.length === 0 ? null : categoryId,
          deviceId: deviceId.length === 0 ? null : deviceId,
          defaultDurationSeconds:
            durationAmount > 0 ? amountToSeconds(durationAmount, durationUnit) : null,
          defaultDurationUnit: durationAmount > 0 ? durationUnit : null,
          contraindication: contraindication.trim().length === 0 ? null : contraindication.trim(),
          bodyAreaIds: selectedAreaIds,
          isActive,
        },
      });
      if (pendingImageFile !== null) {
        await uploadImage.mutateAsync({ id: saved.id, file: pendingImageFile });
      } else if (pendingImageRemoved && treatment !== null && treatment.imageUrl !== null) {
        await removeImage.mutateAsync(saved.id);
      }
      showSuccessNotification(isEditing ? t('form.updated') : t('form.saved'));
      onClose();
    } catch (error) {
      showErrorNotification(resolveApiErrorMessage(error));
    }
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="bg-zoowhite w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-5 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender/40">
          <h3 className="text-lg font-bold text-zooindigo">
            {isEditing ? t('form.editTitle') : t('form.addTitle')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('form.cancel')}
            onClick={onClose}
            className="rounded-full"
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>
                {t('form.nameEn')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                value={nameEn}
                hasError={showErrors && nameEnMissing}
                placeholder={t('form.nameEnPlaceholder')}
                onChange={function update(changeEvent) {
                  setNameEn(changeEvent.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>
                {t('form.namePl')} <span className="text-zooerrortext">*</span>
              </Label>
              <Input
                value={namePl}
                hasError={showErrors && namePlMissing}
                placeholder={t('form.namePlPlaceholder')}
                onChange={function update(changeEvent) {
                  setNamePl(changeEvent.target.value);
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>{t('form.category')}</Label>
              <SearchableSelect
                inputId="treatment-category"
                value={categoryId}
                options={categories.map(function toOption(category) {
                  return {
                    value: category.id,
                    label: resolveLocalizedName(category.nameEn, category.namePl),
                  };
                })}
                onChange={function update(nextValue) {
                  setCategoryId(nextValue);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>{t('form.defaultDuration')}</Label>
              <DurationInput
                amount={durationAmount}
                unit={durationUnit}
                ariaLabel={t('form.defaultDuration')}
                onChange={function update(nextAmount, nextUnit) {
                  setDurationAmount(nextAmount);
                  setDurationUnit(nextUnit);
                }}
              />
              {treatment !== null && treatment.defaultDurationLegacyText !== null ? (
                <p className="text-xs text-zoomuted mt-1.5">
                  {t('form.defaultDurationLegacyLabel')}: {treatment.defaultDurationLegacyText}
                  <br />
                  {t('form.defaultDurationLegacyHint')}
                </p>
              ) : null}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('image.alt')}</Label>
            <EntityImageUpload
              imageUrl={imageUrl}
              onSelect={handleSelectImage}
              onRemove={handleRemoveImage}
              isBusy={isSaving}
            />
          </div>

          <div className="space-y-1">
            <Label>{t('form.device')}</Label>
            <SearchableSelect
              inputId="treatment-device"
              value={deviceId}
              placeholder={t('form.deviceNone')}
              isClearable
              options={devices.map(function toOption(device) {
                return {
                  value: device.id,
                  label: resolveLocalizedName(device.nameEn, device.namePl),
                };
              })}
              onChange={function update(nextValue) {
                setDeviceId(nextValue);
              }}
            />
            {selectedDevice === undefined || selectedDevice.settingFields.length === 0 ? (
              <p className="text-xs text-zoomuted mt-1.5">
                {t('form.deviceHint')}{' '}
                <Button variant="link" onClick={onManageDevices} className="font-semibold">
                  {t('form.manageDevices')}
                </Button>
              </p>
            ) : (
              <p className="text-xs text-zoomuted mt-1.5">
                {t('form.deviceRecords')}{' '}
                {selectedDevice.settingFields
                  .map(function toName(field) {
                    return field.name;
                  })
                  .join(' · ')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('form.bodyAreas')}</Label>
            <div className="flex flex-wrap gap-2">
              {bodyAreas.map(function renderArea(area) {
                const selected = selectedAreaIds.includes(area.id);
                return (
                  <button
                    key={area.id}
                    type="button"
                    onClick={function pick() {
                      toggleArea(area.id);
                    }}
                    className={
                      selected
                        ? 'px-3 py-1.5 rounded-lg text-[13px] font-semibold border border-zooprimary bg-zooprimary text-zoowhite'
                        : 'px-3 py-1.5 rounded-lg text-[13px] font-semibold border border-zooborder bg-zoooffwhite text-zoomuted hover:border-zooprimary'
                    }
                  >
                    {formatBilingualName(area.nameEn, area.namePl)}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={onManageBodyAreas}
                className="px-3 py-1.5 rounded-lg text-[13px] font-semibold border border-dashed border-zoopalelavender text-zooprimary bg-zoowhite"
              >
                {t('form.manageAreas')}
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <Label>{t('form.contraindication')}</Label>
            <Textarea
              rows={2}
              value={contraindication}
              placeholder={t('form.contraindicationPlaceholder')}
              onChange={function update(changeEvent) {
                setContraindication(changeEvent.target.value);
              }}
            />
          </div>
        </div>

        <div className="p-5 border-t border-zooborder flex items-center justify-between bg-zoooffwhite">
          <label className="flex items-center gap-2.5 text-[13px] font-semibold text-zooindigo">
            <ToggleSwitch
              isChecked={isActive}
              onToggle={function toggle() {
                setIsActive(function invert(previous) {
                  return previous === false;
                });
              }}
              accessibleLabel={t('form.activeLabel')}
            />
            {t('form.activeLabel')}
          </label>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button type="button" isLoading={isSaving} onClick={save}>
              {t('form.save')}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
